/*
    Copyright © DokiApp, 2025
*/

import React, { useEffect, useState } from 'react'
import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useStoreActions, useStoreState } from '../../store/store.hooks'
import { Heading } from '../../components/Text'
import { NavigateBack } from '../../components/Nav/NavigateBack'
import { GradientButton, WhiteButton } from '../../components/Button'
import { get } from '../../services/request'
import { useRelativeNavigate } from '../../hooks/useRelativeNavigate'
import { MYMEDIO_URLS } from '../../constants'
import { EvaluationBandBar } from './EvaluationBandBar'

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  buttonContainer: {
    margin: '0 2rem 2rem',
  },
  centered: {
    textAlign: 'center',
  },
  consultationButton: {
    width: '90%',
    boxShadow: '0 10px 20px 0 rgba(100,115,175,0.4)',
    border: '1px solid #6473AF',
  },
  disclaimer: {
    margin: '0 2rem 2rem',
    fontSize: '1.1rem',
    textAlign: 'justify',
  },
  evaluationDescription: {
    fontSize: '1.1rem',
    fontWeight: '600',
    textAlign: 'justify',
  },
  evaluationDescriptionBox: {
    margin: '0 2rem 2rem',
    padding: '1rem',
    border: '1px solid rgba(30, 40, 100, 0.15)',
    borderRadius: '0.5rem',
  },
  factorCard: {
    textAlign: 'center',
    padding: '1rem 0.5rem',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },
  factorLevel: {
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  factorName: {
    fontWeight: '600',
    fontSize: '0.85rem',
    color: '#1E2864',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
  },
  factorsContainer: {
    margin: '0 2rem 2rem',
    border: '1px solid rgba(30, 40, 100, 0.15)',
    borderRadius: '0.5rem',
    padding: '0.5rem 0',
  },
  factorsSectionTitle: {
    margin: '0 2rem 1rem',
    fontWeight: '600',
    fontSize: '1rem',
    color: '#1E2864',
  },
  infoText: {
    margin: '0 2rem 2rem',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '0.95rem',
    color: '#1E2864',
  },
  page: {
    backgroundColor: '#fff',
    borderRadius: '15px',
  },
  pageHeader: {
    marginBottom: theme.spacing(3),
  },
  schedulingButton: {
    width: '90%',
    boxShadow: '0 10px 20px 0 rgba(100,115,175,0.4)',
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
      marginBottom: 30,
    },
  },
  sectionTitle: {
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '1.1rem',
    color: '#1E2864',
    margin: '1rem 2rem',
  },
}))

const MentalWellbeingEvaluation = () => {
  const classes = useStyles()
  const {
    t,
    i18n: { language },
  } = useTranslation('survey')
  const { relativeNavigate } = useRelativeNavigate()
  const { fetchSettings } = useStoreActions((actions) => actions.settings)
  const { settingsByKey } = useStoreState((state) => state.settings)

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchEvaluation = async () => {
      try {
        setLoading(true)
        const result = await get(
          `/surveys/mental-wellbeing-diagnostics/evaluation?lng=${language}`
        )
        setData(result)
      } catch (err) {
        console.error('Failed to fetch mental wellbeing evaluation', err)
      } finally {
        setLoading(false)
      }
    }

    fetchEvaluation()
  }, [])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const handleScheduleClick = () => {
    const url = settingsByKey[MYMEDIO_URLS.THERAPIST]
    if (url) {
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
      if (newWindow) newWindow.opener = null
    }
  }

  const handleBackToDashboard = () => {
    relativeNavigate('/')
  }

  if (loading) {
    return (
      <Backdrop className={classes.backdrop} open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    )
  }

  if (!data) {
    return null
  }

  return (
    <Grid container className={classes.page}>
      <NavigateBack />
      <Grid item xs={12} className={classes.pageHeader}>
        <Heading>{t('mentalWellbeingEvalHeader')}</Heading>
      </Grid>

      <Grid item xs={12}>
        <Typography className={classes.disclaimer}>
          {t('mentalWellbeingEvalDisclaimer')}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography className={classes.sectionTitle}>
          {t('mentalWellbeingEvalOverallTitle')}
        </Typography>
      </Grid>

      <EvaluationBandBar
        evaluations={data.evaluations}
        evaluation={data.evaluation}
      />

      <Grid item xs={12}>
        <Box className={classes.evaluationDescriptionBox}>
          <Typography className={classes.evaluationDescription}>
            {data.evaluation?.description}
          </Typography>
        </Box>
      </Grid>

      {/* Factor section */}
      <Grid item xs={12}>
        <Typography className={classes.factorsSectionTitle}>
          {t('mentalWellbeingEvalFactorsTitle')}
        </Typography>
      </Grid>

      <Grid container className={classes.factorsContainer}>
        {data.factors.map((factor) => (
          <Grid item xs={6} sm={3} key={factor.name}>
            <Box className={classes.factorCard}>
              <Typography className={classes.factorName}>
                {factor.name}
              </Typography>
              <Typography className={classes.factorLevel}>
                {factor.level}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Grid item xs={12}>
        <Typography className={classes.infoText}>
          {t('mentalWellbeingEvalInfoText')}
        </Typography>
      </Grid>

      <Grid
        container
        justifyContent="center"
        spacing={2}
        className={classes.buttonContainer}
      >
        <Grid item xs={6} className={classes.centered}>
          <GradientButton
            className={classes.schedulingButton}
            onClick={handleScheduleClick}
          >
            {t('mentalWellbeingEvalScheduleButton')}
          </GradientButton>
        </Grid>
        <Grid item xs={6} className={classes.centered}>
          <WhiteButton
            className={classes.consultationButton}
            onClick={handleBackToDashboard}
          >
            {t('mentalWellbeingEvalBackButton')}
          </WhiteButton>
        </Grid>
      </Grid>
    </Grid>
  )
}

MentalWellbeingEvaluation.displayName = 'MentalWellbeingEvaluation'

export default MentalWellbeingEvaluation
export { MentalWellbeingEvaluation }
